import Avo from '@susu/custom-made/Avo';
import { CardListType } from '@susu/custom-made/components/cardList/cardList';
import { useAdditionalProduct } from '@susu/custom-made/hooks/useAdditionalProduct';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import useScrollUp from '@susu/custom-made/hooks/useScrollUp';
import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import {
  applyBusinessRulesForOptionSelection,
  isOptionHiddenByRule,
  isOptionValueHiddenByRule,
} from '@susu/custom-made/utils/businessRules';
import { Clients, FabricViewMode, Level } from '@susu/custom-made/utils/constants';
import type {
  BusinessRules,
  DefaultValues,
  HiddenOptionsAndValues,
  LayerDefinitions,
  Option,
  OptionsSelection,
  OptionValue,
  StylePreset,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { changeLevel, getConfigurationTypeBySection } from '@susu/custom-made/utils/navigation';
import { OPTION_PANEL_TEST_IDS } from '@susu/custom-made/utils/testIds';
import {
  getConfigurationTypeKey,
  getItemCountKey,
  getProductTypeKey,
  isExtraConfigurationInProduct,
  isProductBaseStyleActive,
  onEnter,
  shouldAddAdditonalProduct,
  shouldShowRemoveAdditionalProduct,
} from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Carousel from '../baseStyleCarousel/baseStyleCarousel';
import Button, { ButtonSize, ButtonType } from '../button/button';
import type { typeCard } from '../card/card';
import Modal from '../modal/modal';
import { MonogramPanel } from '../monogramPanel/monogramPanel';
import type { Item } from '../navigation/navigation';
import Panel, { PanelSize } from '../panel/panel';
import { PanelSearchHeader } from '../panelSearchHeader/panelSearchHeader';
import Tooltip from '../tooltip/tooltip';
const TRANSITION_ALL = '[transition:all_0.4s_cubic-bezier(0.5,0.05,0.1,1)]';

const getSectionMainClasses = (isActive: boolean) =>
  cn(
    `z-[11] ${TRANSITION_ALL} h-[188px] md:h-[244px] lg:h-full w-full lg:shrink-0 flex flex-col`,
    isActive
      ? 'visible translate-y-0 lg:translate-x-0'
      : 'invisible translate-y-full lg:translate-y-0 lg:translate-x-full',
  );

const getSectionNextClasses = (isActive: boolean) =>
  cn(
    `z-10 ${TRANSITION_ALL} relative h-[188px] md:h-[244px] lg:h-full w-full lg:shrink-0 flex flex-col`,
    isActive ? 'visible -translate-y-full lg:translate-y-0 lg:-translate-x-full' : 'invisible',
  );

const getCardListClasses = (isSubList: boolean, hasSearch: boolean) => {
  const searchPaddingClasses = hasSearch ? 'lg:pt-[122px] 2xl:pt-[128px]' : 'lg:pt-[82px] 2xl:pt-[88px]';

  return cn(
    'flex justify-center lg:overflow-auto lg:w-full',
    isSubList
      ? cn('lg:h-[calc(100dvh-var(--webstore-header-height))]', searchPaddingClasses)
      : 'lg:h-[calc(100dvh-73px-var(--webstore-header-height))] 2xl:h-[calc(100dvh-77px-var(--webstore-header-height))]',
  );
};

const HEADER_CLASSES =
  'text-center text-sm font-medium leading-sm p-0 h-56 md:text-lg md:leading-[26px] md:px-[118px] lg:absolute lg:z-[2] lg:top-0 lg:left-0 lg:right-0 lg:h-[82px] lg:text-base lg:leading-base lg:bg-white 2xl:text-xl 2xl:leading-lg 2xl:h-[88px]';

/** The back button is absolute, so only the left gutter is taken; the right one keeps a title centred. */
const HEADER_TITLE_GUTTERS = 'lg:px-56 2xl:px-60';

/**
 * Nothing sits to the right of the tab row at sub-group level (the Finish button is group-level
 * only), so it keeps the left clearance and spends the rest on tabs, with `Navigation`'s own
 * `lg:px-20` left as the gutter. Buys 56px before the overflow chevrons appear.
 */
const HEADER_NAV_GUTTERS = 'lg:pl-56 lg:pr-0 2xl:pl-60';

const SEARCH_CONTAINER_CLASSES =
  "absolute top-6 right-4 text-xl z-1 cursor-pointer h-44 w-44 flex items-center justify-center md:p-20 md:top-[25px] md:right-20 lg:relative lg:inset-auto lg:after:content-[''] lg:after:absolute lg:after:-inset-[6px] lg:after:rounded-full lg:p-0 lg:w-20 lg:h-20 lg:justify-self-start lg:text-sm lg:leading-sm lg:rounded-full lg:gap-4";

const BUTTON_CONTAINER_CLASSES =
  'absolute top-6 left-[11px] md:top-[15px] md:left-16 lg:top-20 lg:left-20 lg:z-[2] 2xl:top-20 2xl:left-24';

const OPTION_TITLE_CLASSES =
  'text-gray-800 text-center text-sm font-medium leading-sm h-56 flex justify-center items-center md:text-lg md:leading-[26px] lg:text-base lg:leading-base lg:h-[82px] 2xl:text-xl 2xl:leading-lg';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useGlobalContext from '@susu/custom-made/providers/hooks/useGlobalContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import Loader from '../loader/loader';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { useAvo } from '@susu/custom-made/hooks/useAvo';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';
import { useI18N } from '@susu/i18n/client';
import { formatPrice } from '@susu/custom-made/utils/formatPrice';

const { SHOW_LOGS, ENABLE_SCROLL_TO_SELECTED, ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

const SearchList = lazy(() => import('../searchList/searchList'));
const Navigation = lazy(() => import('../navigation/navigation'));
const CardList = lazy(() => import('@susu/custom-made/components/cardList/cardList'));

const OptionPanelComponent = ({
  navigation,
  extraProductHeader,
  stylePresets,
  options,
  optionValues,
  businessRules,
  defaultValues,
  layerDefinitions,
  isInPreloadFlow,
  fabricViewMode,
}: {
  navigation: React.ReactNode;
  extraProductHeader: React.ReactNode;
  stylePresets: StylePreset[];
  options: Option[];
  optionValues: typeOptionValue;
  businessRules: BusinessRules;
  defaultValues: DefaultValues;
  layerDefinitions: LayerDefinitions;
  isInPreloadFlow: boolean;
  fabricViewMode: FabricViewMode;
}) => {
  const { t } = useI18N();
  const defaultOptionValueKeys = Object.keys(optionValues);
  const { section, level, setLevel } = useNavigationContext();
  const [isSearchShown, setIsSearchShown] = useState<boolean>(false);
  const [filteredOptionValueIds, setFilteredOptionValueIds] = useState<number[] | string[]>(defaultOptionValueKeys);
  const [isStylePresetModalOpen, setIsStylePresetModalOpen] = useState<boolean>(false);
  const viewSize = useViewSize();
  const cardlistRef = useRef<HTMLDivElement>(null);
  const scrollUp = useScrollUp();
  const { removeSelectedOptionValueFromMissingValues, isOptionWithoutSelection } = useMissingValues();
  const affectedConfigurationTypeId = useMemo(() => getConfigurationTypeBySection(section!), [section]);
  const { addAdditionalProductHandler, removeAdditionalProductHandler } = useAdditionalProduct();
  const { selectedFabric } = useFabricContext();
  const { isManualSelectionDone, setIsManualSelectionDone } = useGlobalContext();
  const { client, product } = useUserContext();
  const { getAvoStandardEventParams } = useAvo();
  const {
    selectedOptions,
    hiddenOptionsAndValues,
    selectedBaseStyle,
    isAdditionalProductAdded,
    isBaseStyleSelection,
    selectedOption,
    selectedOptionGroup,
    setAnimation,
    setSelectedOption,
    setSelectedOptionGroup,
    updateHiddenOptionsAndValues,
    setIsBaseStyleSelection,
    selectOption,
  } = useOptionContext();
  // The React Compiler would hoist `selectedOption.id` from the callbacks into render, throwing when unset.
  const selectedOptionId = selectedOption?.id;

  // Business rules can hide individual values, so count what the card list actually renders.
  const visibleOptionValueCount = useMemo(
    () =>
      (filteredOptionValueIds as (number | string)[]).filter(
        id => !isOptionValueHiddenByRule(Number(id), hiddenOptionsAndValues, affectedConfigurationTypeId),
      ).length,
    [filteredOptionValueIds, hiddenOptionsAndValues, affectedConfigurationTypeId],
  );

  const stylePresetModalKey =
    isInPreloadFlow && !isProductBaseStyleActive(selectedBaseStyle, section) ? 'customizeFlow' : 'normalFlow';
  const showLogsFeatureEnabled = useFeatureIsOn(SHOW_LOGS);
  const scrollToSelectedFeatureEnabled = useFeatureIsOn(ENABLE_SCROLL_TO_SELECTED);
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);

  const optionNavigationClick = (option: Option | Item) => {
    const tmpOption = option as Option;
    setAnimation({ status: true, from: selectedOption, to: tmpOption });
    setSelectedOption(tmpOption);
    cardlistRef.current?.scrollTo(0, 0);

    const cmOption = `${selectedOptionGroup?.name}_${tmpOption.name}`;

    if (isAvoTrackingEnabled) {
      Avo.cmSelectionClicked({
        ...getAvoStandardEventParams(`${section}_options`),
        cmItemType: tmpOption?.isAdvanced ? Avo.CmItemType.OPTION_ADVANCED : Avo.CmItemType.OPTION,
        currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
        cmItemValue: cmOption.toLowerCase(),
        cmSelectedView: fabricViewMode === FabricViewMode.Layers ? Avo.CmSelectedView.PRODUCT_VIEW : Avo.CmSelectedView.MODEL_VIEW,
      });
    }
  };

  const hasOptionGroupNav = !!selectedOptionGroup && !!selectedOption;

  const optionNavigation = (
    <>
      {selectedOptionGroup && selectedOption && (
        <nav>
          <Suspense fallback={<Loader />}>
            <Navigation
              items={[
                ...selectedOptionGroup.options!.map(option => {
                  if (!isOptionHiddenByRule(option.id, hiddenOptionsAndValues, affectedConfigurationTypeId)) {
                    return {
                      text: option.name,
                      id: option.id.toString(),
                      option: option,
                      isActive: selectedOptionId === option.id,
                      hasMissingValues: isOptionWithoutSelection(option, affectedConfigurationTypeId),
                    } as Item;
                  }

                  return undefined;
                }),
              ]}
              testId={OPTION_PANEL_TEST_IDS.GROUP_NAV}
              onItemClick={optionNavigationClick}
            />
          </Suspense>
        </nav>
      )}
      {!selectedOptionGroup && selectedOption && (
        <div data-testid={selectedOption.id} className={OPTION_TITLE_CLASSES}>
          {selectedOption.name}
        </div>
      )}
    </>
  );

  const selectOptionHandler = useCallback(
    (card: OptionValue) => {
      if (!isManualSelectionDone) {
        setIsManualSelectionDone(true);
      }

      const previousSelectedOptions = structuredClone(selectedOptions);
      const previousHiddenOptionsAndValues = structuredClone(hiddenOptionsAndValues);

      const selectedOptionsTmp: OptionsSelection = structuredClone(selectedOptions);

      const prevValueId = selectedOptionsTmp[affectedConfigurationTypeId][selectedOptionId!];

      selectedOptionsTmp[affectedConfigurationTypeId][selectedOptionId!] = card.id;

      const tmpHiddenOptionsAndValues: HiddenOptionsAndValues = structuredClone(hiddenOptionsAndValues);

      //remove prvious hiddens
      applyBusinessRulesForOptionSelection(
        selectedOptionsTmp,
        selectedFabric!.id,
        businessRules,
        tmpHiddenOptionsAndValues,
        optionValues,
        defaultValues,
        card.id,
        affectedConfigurationTypeId,
        options,
        product!,
        showLogsFeatureEnabled,
        prevValueId,
      );

      updateHiddenOptionsAndValues(tmpHiddenOptionsAndValues);
      selectOption(selectedOptionsTmp);

      removeSelectedOptionValueFromMissingValues(affectedConfigurationTypeId, card);

      window.DD_RUM?.addAction('Custom made - Option Clicked', {
        customData: {
          previousSelectedOptions,
          previousHiddenOptionsAndValues,
          previousOptionValueId: prevValueId,
          selectedFabric: selectedFabric,
          selectedOptions: selectedOptionsTmp,
          hiddenOptionsAndValues: tmpHiddenOptionsAndValues,
          selectedOptionValue: card,
        },
      });

      const cmOption =
        (selectedOptionGroup?.name ? `${selectedOptionGroup?.name}_` : '') +
        `${selectedOption?.name}_${
          optionValues[selectedOptionsTmp[affectedConfigurationTypeId][selectedOptionId!]]?.name ?? ''
        }`;

      if (isAvoTrackingEnabled) {
        Avo.cmSelectionClicked({
          ...getAvoStandardEventParams(`${section}_options`),
          cmItemType: selectedOption?.isAdvanced ? Avo.CmItemType.OPTION_ADVANCED : Avo.CmItemType.OPTION,
          currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
          cmItemValue: cmOption.toLowerCase(),
          cmSelectedView: fabricViewMode === FabricViewMode.Layers ? Avo.CmSelectedView.PRODUCT_VIEW : Avo.CmSelectedView.MODEL_VIEW,
        });
      }
    },
    [
      isManualSelectionDone,
      setIsManualSelectionDone,
      selectedOptions,
      selectedFabric,
      selectedOptionGroup?.name,
      selectedOption,
      selectedOptionId,
      hiddenOptionsAndValues,
      optionValues,
      defaultValues,
      options,
      businessRules,
      product,
      showLogsFeatureEnabled,
      removeSelectedOptionValueFromMissingValues,
      updateHiddenOptionsAndValues,
      selectOption,
      getAvoStandardEventParams,
      section,
      selectedBaseStyle,
      affectedConfigurationTypeId,
      isAvoTrackingEnabled,
      fabricViewMode,
    ],
  );

  const monogramCardClick = useCallback(
    (card: typeCard) => {
      changeLevel(Level.Monogram, setLevel);
      if (isAvoTrackingEnabled) {
        Avo.cmSelectionClicked({
          ...getAvoStandardEventParams(`${section}_options`),
          cmItemType: Avo.CmItemType.OPTION,
          currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
          cmItemValue: card.name.toLowerCase(),
          cmSelectedView: fabricViewMode === FabricViewMode.Layers ? Avo.CmSelectedView.PRODUCT_VIEW : Avo.CmSelectedView.MODEL_VIEW,
        });
      }
    },
    [isAvoTrackingEnabled, getAvoStandardEventParams, section, selectedBaseStyle, affectedConfigurationTypeId, setLevel, fabricViewMode],
  );

  const optionGroupCardClick = useCallback(
    (card: typeCard) => {
      let tmpSelectedOption: Option | undefined = undefined;
      (card as Option).options?.forEach(option => {
        if (
          !tmpSelectedOption &&
          !isOptionHiddenByRule(option.id, hiddenOptionsAndValues, affectedConfigurationTypeId)
        ) {
          tmpSelectedOption = option;
        }
      });
      setAnimation({ status: true, from: undefined, to: tmpSelectedOption });
      changeLevel(Level.Subgroup, setLevel);

      if (tmpSelectedOption) {
        setSelectedOptionGroup(card as Option);
        setSelectedOption(tmpSelectedOption);

        const cmOption = `${card.name}_${(tmpSelectedOption as Option).name}`;
        if (isAvoTrackingEnabled) {
          Avo.cmSelectionClicked({
            ...getAvoStandardEventParams(`${section}_options`),
            cmItemType: (tmpSelectedOption as Option).isAdvanced
              ? Avo.CmItemType.OPTION_ADVANCED
              : Avo.CmItemType.OPTION,
            currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
            cmItemValue: cmOption.toLowerCase(),
            cmSelectedView: fabricViewMode === FabricViewMode.Layers ? Avo.CmSelectedView.PRODUCT_VIEW : Avo.CmSelectedView.MODEL_VIEW,
          });
        }
      }
    },
    [
      hiddenOptionsAndValues,
      isAvoTrackingEnabled,
      getAvoStandardEventParams,
      section,
      selectedBaseStyle,
      affectedConfigurationTypeId,
      setLevel,
      setAnimation,
      setSelectedOptionGroup,
      setSelectedOption,
      fabricViewMode,
    ],
  );

  const optionCardClick = useCallback(
    (card: typeCard) => {
      const tmpSelectedOption = card as Option;
      setAnimation({ status: true, from: undefined, to: tmpSelectedOption });
      changeLevel(Level.Subgroup, setLevel);
      setSelectedOptionGroup(undefined);
      setSelectedOption(tmpSelectedOption);

      const cmOption = tmpSelectedOption.name;
      if (isAvoTrackingEnabled) {
        Avo.cmSelectionClicked({
          ...getAvoStandardEventParams(`${section}_options`),
          cmItemType: tmpSelectedOption?.isAdvanced ? Avo.CmItemType.OPTION_ADVANCED : Avo.CmItemType.OPTION,
          currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
          cmItemValue: cmOption.toLowerCase(),
          cmSelectedView: fabricViewMode === FabricViewMode.Layers ? Avo.CmSelectedView.PRODUCT_VIEW : Avo.CmSelectedView.MODEL_VIEW,
        });
      }
    },
    [
      isAvoTrackingEnabled,
      getAvoStandardEventParams,
      section,
      selectedBaseStyle,
      affectedConfigurationTypeId,
      setLevel,
      setAnimation,
      setSelectedOptionGroup,
      setSelectedOption,
      fabricViewMode,
    ],
  );

  const cardClick = useCallback(
    (card: typeCard) => {
      if (level === Level.Group) {
        if ((card as Option).type === 'MONOGRAM') {
          monogramCardClick(card);
        } else if ((card as Option).type === 'OPTION_GROUP') {
          optionGroupCardClick(card);
        } else {
          optionCardClick(card);
        }
      } else {
        selectOptionHandler(card as OptionValue);
      }
    },
    [level, monogramCardClick, optionGroupCardClick, optionCardClick, selectOptionHandler],
  );

  const searchCardClick = useCallback(
    (card: typeCard) => {
      selectOptionHandler(card as OptionValue);
    },
    [selectOptionHandler],
  );

  const confirmClick = () => {
    const cmOption: string =
      (selectedOptionGroup?.name ? `${selectedOptionGroup?.name}_` : '') +
      `${selectedOption?.name}_${
        optionValues[selectedOptions[affectedConfigurationTypeId][selectedOptionId!]]?.name ?? ''
      }`;

    if (isAvoTrackingEnabled) {
      Avo.cmSelectionConfirmed({
        ...getAvoStandardEventParams(`${section}_options`),
        cmItemType: selectedOption?.isAdvanced ? Avo.CmItemType.OPTION_ADVANCED : Avo.CmItemType.OPTION,
        currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
        cmItemValue: cmOption.toLowerCase(),
      });
    }

    setAnimation({ status: true, from: selectedOption, to: undefined });
    changeLevel(Level.Group, setLevel);
    setTimeout(() => {
      cardlistRef.current?.scrollTo(0, 0);
    }, 400);
  };
  const getProductBaseStyle = () => {
    const selectedStylePreset = selectedBaseStyle[affectedConfigurationTypeId];

    return selectedStylePreset?.name ?? t('optionPanel.noSelectedPreset');
  };

  const baseStyleClick = () => {
    setIsStylePresetModalOpen(true);
    if (isAvoTrackingEnabled) {
      Avo.cmEditClicked({
        ...getAvoStandardEventParams(`${section}_options`),
        cmItemType: Avo.CmItemType.BASE_STYLE,
        currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
        cmItemValue: getProductBaseStyle().toLowerCase() ?? '',
      });
    }
  };

  const changeStyleClick = () => {
    setIsStylePresetModalOpen(false);
    setIsBaseStyleSelection(true);

    if (isAvoTrackingEnabled) {
      Avo.cmEditConfirmed({
        ...getAvoStandardEventParams(`${section}_options`),
        cmItemType: Avo.CmItemType.BASE_STYLE,
        currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
        cmItemValue: getProductBaseStyle().toLowerCase() ?? '',
      });
    }
  };

  const changeStyleCancel = () => {
    setIsStylePresetModalOpen(false);
    if (isAvoTrackingEnabled) {
      Avo.cmEditCanceled({
        ...getAvoStandardEventParams(`${section}_options`),
        cmItemType: Avo.CmItemType.BASE_STYLE,
        currentBaseStyle: selectedBaseStyle[affectedConfigurationTypeId]?.name?.toLowerCase(),
        cmItemValue: getProductBaseStyle().toLowerCase() ?? '',
      });
    }
  };

  const shouldShowBaseStyleSelection = () => {
    return stylePresets.length > 0 && isBaseStyleSelection;
  };

  const openSearchPanel = () => {
    setIsSearchShown(true);
    if (isAvoTrackingEnabled) {
      Avo.searchClicked({
        ...getAvoStandardEventParams(`${section}_options`),
      });
    }
  };

  useEffect(() => {
    if (level === Level.Group) {
      setIsSearchShown(false);
      setIsStylePresetModalOpen(false);
    }
  }, [level]);

  useEffect(() => {
    if (selectedOption) {
      setFilteredOptionValueIds(selectedOption.optionValues!);
    }
  }, [selectedOption, setFilteredOptionValueIds]);

  // Scroll to selected item on page load
  useEffect(() => {
    const selectedItemId =
      selectedOptionId && level === Level.Subgroup
        ? selectedOptions[affectedConfigurationTypeId][selectedOptionId]
        : null;

    if (selectedItemId && cardlistRef.current && level === Level.Subgroup && scrollToSelectedFeatureEnabled) {
      // Use setTimeout to ensure the DOM is fully rendered and animation is completed
      const timeoutId = setTimeout(() => {
        const container = cardlistRef.current;
        const selectedCard = container?.querySelector(`[data-testid="${selectedItemId}"]`) as HTMLElement | null;
        const containerSubOptions = container?.querySelector(`[data-testid="${OPTION_PANEL_TEST_IDS.SUBGROUP_LIST}"]`);
        if (container && selectedCard) {
          const isMobileOrTablet = viewSize === ViewSize.Mobile || viewSize === ViewSize.Tablet;

          if (isMobileOrTablet && containerSubOptions) {
            // Horizontal scroll for mobile/tablet
            const containerWidth = container.clientWidth;
            const cardOffsetLeft = selectedCard.offsetLeft;
            const cardWidth = selectedCard.offsetWidth;
            const scrollLeft = cardOffsetLeft - containerWidth / 2 + cardWidth / 2;

            containerSubOptions.scrollTo({
              left: scrollLeft,
              behavior: 'smooth',
            });
          } else {
            // Vertical scroll for desktop
            const containerHeight = container.clientHeight;
            const cardOffsetTop = selectedCard.offsetTop;
            const cardHeight = selectedCard.offsetHeight;
            const scrollTop = cardOffsetTop - containerHeight / 2 + cardHeight / 2;

            container.scrollTo({
              top: scrollTop,
              behavior: 'smooth',
            });
          }
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [
    selectedOption,
    affectedConfigurationTypeId,
    level,
    filteredOptionValueIds,
    scrollToSelectedFeatureEnabled,
    viewSize,
  ]);

  return (
    <>
      {!shouldAddAdditonalProduct(isAdditionalProductAdded, product!, section) &&
        shouldShowBaseStyleSelection() &&
        section && (
          <div>
            <Carousel
              stylePresets={stylePresets}
              section={section}
              optionValues={optionValues}
              layerDefinitions={layerDefinitions}
              businessRules={businessRules}
              options={options}
              fabricViewMode={fabricViewMode}
            />
          </div>
        )}
      {shouldAddAdditonalProduct(isAdditionalProductAdded, product!, section) && (
        <div className={getSectionMainClasses(level !== Level.Subgroup)}>
          {navigation}
          <div className="flex flex-col w-full h-full lg:justify-center">
            <Modal
              isOpen={
                !isAdditionalProductAdded[affectedConfigurationTypeId] &&
                !isExtraConfigurationInProduct(product!, affectedConfigurationTypeId)
              }
              hasOverlay={false}
              title={
                <span className="inline-block first-letter:uppercase">
                  {t('optionPanel.addProductModal.title', {
                    configurationType: t(
                      `configurationType.${getConfigurationTypeKey(affectedConfigurationTypeId)}`,
                    ).toLowerCase(),
                  })}
                </span>
              }
              price={
                '+' +
                (selectedFabric
                  ? formatPrice(
                      selectedFabric.prices?.configurationTypes[affectedConfigurationTypeId] ?? 0,
                      selectedFabric.prices?.code ?? '',
                    )
                  : '')
              }
              description={
                <span className="inline-block first-letter:uppercase">
                  {t('optionPanel.addProductModal.description', {
                    product: product ? t(`productType.${getProductTypeKey(product)}`) : '',
                    configurationType: t(
                      `configurationType.${getConfigurationTypeKey(affectedConfigurationTypeId)}`,
                    ).toLowerCase(),
                  })}
                </span>
              }
              firstButtonText={
                <span className="inline-block first-letter:uppercase">
                  {t('optionPanel.addProductModal.addButton', {
                    configurationType: t(
                      `configurationType.${getConfigurationTypeKey(affectedConfigurationTypeId)}`,
                    ).toLowerCase(),
                  })}
                </span>
              }
              firstButtonType={ButtonType.Primary}
              isRectangular={false}
              isButtonContentSize={true}
              onFirstButtonClick={() => addAdditionalProductHandler(affectedConfigurationTypeId)}
              testId={OPTION_PANEL_TEST_IDS.ADD_ADDITIONAL_PRODUCT_MODAL}
            />
          </div>
        </div>
      )}

      {!shouldAddAdditonalProduct(isAdditionalProductAdded, product!, section) && !shouldShowBaseStyleSelection() && (
        <>
          {/* Group section */}
          <div className={getSectionMainClasses(level !== Level.Subgroup)}>
            {navigation}
            {extraProductHeader}
            <div className={getCardListClasses(false, false)}>
              <Suspense fallback={<Loader />}>
                <CardList
                  cardItems={options}
                  cardClick={cardClick}
                  isGroup={true}
                  isBaseStyleActive={
                    isProductBaseStyleActive(selectedBaseStyle, section) || (isInPreloadFlow && stylePresets.length > 0)
                  }
                  baseStyleClick={baseStyleClick}
                  cardListType={CardListType.Option}
                  optionValues={optionValues}
                  showAdvancedButton={options.filter((option: Option) => option.isAdvanced === true).length > 0}
                  showAdvancedItems={client === Clients.inStore}
                  showRemoveAdditionalProductButton={shouldShowRemoveAdditionalProduct(
                    isAdditionalProductAdded,
                    product!,
                    section,
                  )}
                  additionalProductRemoveClick={() => removeAdditionalProductHandler(affectedConfigurationTypeId)}
                  testId={OPTION_PANEL_TEST_IDS.GROUP_LIST}
                  section={section}
                  affectedConfigurationTypeId={affectedConfigurationTypeId}
                />
              </Suspense>
            </div>
          </div>
          {/* Group section */}

          {/* SubGroup section */}
          <div className={getSectionNextClasses(level === Level.Subgroup)}>
            <div className={cn(HEADER_CLASSES, hasOptionGroupNav ? HEADER_NAV_GUTTERS : HEADER_TITLE_GUTTERS)}>
              {optionNavigation}
            </div>
            {selectedOption?.hasSearch && (
              <PanelSearchHeader
                isVisible={scrollUp.scrollingUp}
                itemCountLabel={t(getItemCountKey(selectedOption?.code), { count: String(visibleOptionValueCount) })}
                className="lg:px-20"
                searchSlot={
                  <div
                    className={SEARCH_CONTAINER_CLASSES}
                    data-testid={OPTION_PANEL_TEST_IDS.SEARCH_BTN}
                    onClick={openSearchPanel}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      onEnter(e, openSearchPanel);
                    }}
                  >
                    <i className="susu-icons lg:text-xl lg:leading-sm lg:pt-0" translate="no">
                      search
                    </i>
                  </div>
                }
              />
            )}
            <div className={BUTTON_CONTAINER_CLASSES}>
              <Button
                isFullSize={true}
                onClick={confirmClick}
                type={ButtonType.IconOnlyRound}
                size={ButtonSize.Large}
                testId={OPTION_PANEL_TEST_IDS.CONFIRM_BTN_MOBILE}
              >
                <div className="flex items-center justify-center w-full">
                  <div className="flex text-gray-800 text-base md:text-xl">
                    <i className="susu-icons" translate="no">
                      arrow_left
                    </i>
                  </div>
                </div>
              </Button>
            </div>
            <div
              className={getCardListClasses(level === Level.Subgroup, !!selectedOption?.hasSearch)}
              ref={cardlistRef}
              onScroll={e => {
                scrollUp.handleScroll(e, 43);
              }}
            >
              <Suspense fallback={<Loader />}>
                <CardList
                  cardItems={optionValues}
                  cardSubItems={filteredOptionValueIds}
                  selectedItemId={
                    selectedOption?.id ? selectedOptions[affectedConfigurationTypeId][selectedOption?.id] : null
                  }
                  cardClick={cardClick}
                  isGroup={false}
                  cardListType={CardListType.Option}
                  testId={OPTION_PANEL_TEST_IDS.SUBGROUP_LIST}
                  section={section}
                  selectedOption={selectedOption}
                  affectedConfigurationTypeId={affectedConfigurationTypeId}
                  isGrid={selectedOption?.hasSearch}
                />
              </Suspense>
            </div>
            {/* Subgroup section */}
          </div>

          {/* Search Panel */}
          <Panel
            isActive={isSearchShown}
            closeClick={() => {
              setIsSearchShown(false);
            }}
            panelSize={PanelSize.Multi}
            testIdSubfix="option-search"
          >
            {isSearchShown && (
              <Suspense fallback={<Loader />}>
                <SearchList
                  onClose={() => {
                    setIsSearchShown(false);
                  }}
                  itemList={optionValues}
                  filteredItemListIds={filteredOptionValueIds}
                  selectedItemId={
                    selectedOption?.id ? selectedOptions[affectedConfigurationTypeId][selectedOption?.id] : null
                  }
                  cardClick={searchCardClick}
                  cardListType={CardListType.Option}
                  showScanner={false}
                  selectedOption={selectedOption}
                  affectedConfigurationTypeId={affectedConfigurationTypeId}
                />
              </Suspense>
            )}
          </Panel>
          <MonogramPanel
            isMonogramShown={level === Level.Monogram}
            options={options}
            configurationTypeId={affectedConfigurationTypeId}
          />
        </>
      )}
      <Modal
        isOpen={isStylePresetModalOpen}
        title={t(`stylePresetModal.${stylePresetModalKey}.title`)}
        description={t(`stylePresetModal.${stylePresetModalKey}.description`)}
        firstButtonText={t(`stylePresetModal.${stylePresetModalKey}.firstButtonText`)}
        firstButtonType={ButtonType.SecondaryAlert}
        secondButtonText={t(`stylePresetModal.${stylePresetModalKey}.secondButtonText`)}
        secondButtonType={ButtonType.Tertiary}
        onFirstButtonClick={changeStyleClick}
        onSecondButtonClick={changeStyleCancel}
        onOverlayClick={changeStyleCancel}
        isFullSize={true}
        testId={OPTION_PANEL_TEST_IDS.SECTION_CHANGE_STYLE_PRESET_MODAL}
      />
      {(viewSize === ViewSize.Mobile || viewSize === ViewSize.Tablet) && <Tooltip tooltipId="optionPanel" />}
    </>
  );
};

OptionPanelComponent.displayName = 'OptionPanel';

export const OptionPanel = memo(OptionPanelComponent);
